import * as React from 'react';
import { cn } from '@/utils/utils';
import { ICON_BUTTON, type IconButtonPlace } from '@/components/ui/recipes';
import { WithTooltip } from '@/components/ui/tooltip';

interface IconButtonProps extends React.ComponentProps<'button'> {
  /** Its accessible name: an icon-only button says what it does. */
  label: string;
  /** panel: in a panel row (close, delete, more); dialog: a dialog's header; overlay: over a 3D preview; hero: over the hero band; toolbar: in a floating row of actions, its icons h-5 (the timeline's asset actions); tag: inside a chip or tag. */
  place?: IconButtonPlace;
  /** A toggle (Keep proportions): on shows the selected look. */
  pressed?: boolean;
  /** destructive: it deletes, its hover the destructive ink (in a panel row); danger: a filled danger disc, for removing a picture; danger-outline: an outlined disc that fills with danger on hover, for deleting a card (a story, a project); brand: a disc filled with the brand ink, for the one action a view is for (a trimmer's play); quiet: faint until hovered, for a card's own menu (a library card's dots); outline: an outlined disc with an h-5 icon, for a row's menu that must be noticed (a story version's options). */
  tone?: 'default' | 'destructive' | 'danger' | 'danger-outline' | 'brand' | 'quiet' | 'outline';
}

/** An icon-only button: round, the theme's hover wash, its icon h-4; its title a themed tooltip, shown when disabled too. */
export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ label, place = 'panel', pressed, tone = 'default', className, type = 'button', title, disabled, children, ...props }, ref) => {
    const button = (
      <button
        ref={ref}
        type={type}
        aria-label={label}
        aria-pressed={pressed}
        disabled={disabled}
        className={cn(
          ICON_BUTTON[place],
          pressed && (place === 'overlay'
            ? 'border-lureo-border-width-medium border-lureo-selected-line bg-lureo-surface text-lureo-on-selected hover:bg-lureo-surface hover:text-lureo-on-selected'
            : 'bg-lureo-brand-ink/10 text-lureo-brand-ink hover:bg-lureo-brand-ink/15 hover:text-lureo-brand-ink'),
          tone === 'destructive' && 'hover:bg-lureo-danger-ink/10 hover:text-lureo-danger-ink',
          tone === 'danger' && 'h-8 w-8 bg-lureo-danger text-lureo-on-emphasis shadow-none ring-1 ring-inset ring-lureo-line-strong/50 hover:bg-lureo-danger-hover hover:text-lureo-on-emphasis active:bg-lureo-danger-pressed',
          tone === 'quiet' && 'text-lureo-ink-subtlest hover:bg-lureo-brand-ink/[0.08] hover:text-lureo-brand-ink',
          tone === 'brand' && 'h-9 w-9 bg-lureo-brand-ink text-lureo-on-brand-ink hover:bg-lureo-brand-ink-pressed hover:text-lureo-on-brand-ink',
          tone === 'danger-outline' && 'h-8 w-8 border-lureo-border-width border-lureo-line-strong bg-lureo-surface/90 text-lureo-ink-subtle backdrop-blur-lureo-blur-sm hover:border-lureo-danger hover:bg-lureo-danger hover:text-lureo-on-emphasis active:bg-lureo-danger-pressed',
          tone === 'outline' && 'h-8 w-8 border-lureo-border-width border-lureo-line-strong text-lureo-ink-subtle hover:border-lureo-link hover:bg-lureo-link/10 hover:text-lureo-link [&_svg]:h-5 [&_svg]:w-5',
          // A disabled button takes no hover, so the span round it shows its tooltip
          title && disabled && 'pointer-events-none',
          className,
        )}
        {...props}
      >
        {children}
      </button>
    );
    if (!title) return button;
    return <WithTooltip content={title}>{disabled ? <span className="inline-flex shrink-0 cursor-not-allowed">{button}</span> : button}</WithTooltip>;
  },
);
IconButton.displayName = 'IconButton';
